import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setUser(data.user);
      navigate(data.user.role === "club_admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="max-w-md text-center fade-up">
          <div className="overline">Link Missing</div>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-3">No reset token found.</h1>
          <p className="text-[#4A4A4A] mt-3">Please use the link from your email, or request a new one.</p>
          <Link to="/forgot-password" className="btn-primary inline-block mt-6">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md fade-up">
        <div className="text-center">
          <div className="overline">Account Security</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-3 leading-none">
            Choose a new password
          </h1>
          <p className="mt-3 text-[#4A4A4A]">
            We&rsquo;ll sign you in once your new password is saved.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-10 surface-card p-8">
          {error && (
            <div
              data-testid={AUTH.errorBanner}
              className="mb-5 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm"
            >
              {error}
            </div>
          )}
          <label className="label-elegant block mb-2">New Password</label>
          <PasswordInput
            required
            minLength={6}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={AUTH.resetPassword}
            className="input-field"
            placeholder="At least 6 characters"
          />
          <label className="label-elegant block mb-2 mt-5">Confirm New Password</label>
          <PasswordInput
            required
            minLength={6}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid={AUTH.resetConfirm}
            className="input-field"
            placeholder="Repeat your new password"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid={AUTH.resetSubmit}
            className="btn-primary w-full mt-6"
          >
            {submitting ? "Saving…" : "Reset Password & Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
